import { useContext, useState } from 'react';

import {
  addLead,
  addProduct,
  addProductClick,
  trackContactFormSubmit,
  trackPhoneLead,
  trackPhoneCall,
  trackPhoneNumberReveal,
  trackYWLeadHygieneExperiment
} from '../../../../store/actions/dataLayer';
import { prepareLeadData, sendLead } from '../../../../utils/api/leadHelper';
import { useDispatch } from 'react-redux';
import { PortalConfigContext } from '../../../../config/portal';
import validate from '../../../../utils/dataValidation';

import {
  prepareLeadEmail,
  prepareLeadName,
  prepareLeadPhone,
  prepareLeadSellerName,
  prepareLeadTextarea
} from '../../utils/bdpParsers';
import { nil } from '../../../../utils/runOnceHelper';
import { useFlagsWithPrefix } from '../../../../utils/countryHelpers';
import { definedValue } from '../../../../utils';

const trackableValues = (leadData, portalLeadsConfiguration) => {
  const {
    types: {
      boat: { type: boatType, contactFormLeadType }
    }
  } = portalLeadsConfiguration;
  const { type } = leadData;
  if (type === boatType) {
    return contactFormLeadType;
  }
  return null;
};

const sendLeadSucceed = (leadData, portalLeadsConfiguration) => {
  return sendLead(leadData)
    .then((resp) => {
      if (resp.status === 200) {
        const trackType = trackableValues(leadData, portalLeadsConfiguration);
        const leadId = resp.data?.id;
        const responseLeadData = { ...leadData };
        responseLeadData.id = leadData.imtId;
        delete responseLeadData.imtId;
        return { trackType, responseLeadData, leadId };
      }
      // eslint-disable-next-line no-console
      console.log('resp', resp);
      return null;
    })
    .catch((err) => {
      // eslint-disable-next-line no-console
      console.log('err', err);
      return null;
    });
};

const useLeadFormTracking = (id) => {
  const dispatch = useDispatch();
  const handlePhoneCall = () => {
    dispatch(trackPhoneCall());
    dispatch(trackPhoneNumberReveal());
    dispatch(addLead(id, 'phone lead'));
    dispatch(trackPhoneLead());
    if (window.kruxCall) {
      window.kruxCall();
    }
  };
  const trackOnSubmit = (type, leadData, leadId, shouldConvertCustomLeadHygieneGoal) => {
    dispatch(addProduct(id));
    dispatch(addProductClick(id));
    dispatch(addLead(id, type));
    dispatch(trackContactFormSubmit({leadId}));
    if (shouldConvertCustomLeadHygieneGoal) {
      dispatch(trackYWLeadHygieneExperiment());
    }
  };
  return { handlePhoneCall, trackOnSubmit };
};

const getInvalidKey = (val, key, name) => {
  return validate(val, key) ? '' : (name || key);
};

const validateAllFields = (name, email, phone, message, featureFlagMandatoryPhone) => {
  const isValidName = getInvalidKey(name, 'fullName', 'name');
  const isValidEmail = getInvalidKey(email, 'email');
  const isValidPhone = featureFlagMandatoryPhone ? getInvalidKey(phone, 'phoneField', 'phone') : getInvalidKey(phone, 'phone');
  const isValidMessage = getInvalidKey(message, 'text');

  return [isValidName, isValidEmail, isValidPhone, isValidMessage].filter(
    (val) => val !== ''
  );
};

// Feel free to split this hook in a more granular way....
const useBdpForm = (
  listing,
  portal,
  translator,
  idPrefix,
  onSuccess = nil,
  onError = nil,
  featureFlagMandatoryPhone,
  showCloseButton,
) => {
  const { messages, t } = translator;
  const context = useContext(PortalConfigContext);
  const cdnUrl = context.client?.cdn?.endpoint;
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const { selectedCountry, flagsWithPrefix } = useFlagsWithPrefix(cdnUrl);
  const [countryPrefix, setCountryPrefix] = useState(definedValue(selectedCountry?.prefix, ''));
  const [message, setMessage] = useState(
    t(messages.contactForm.prePopulatedText)
  );
  const [invalidFields, setInvalidFields] = useState([]);
  const [showError, setShowError] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [isDisabled, setIsDisabled] = useState(false);
  const { handlePhoneCall, trackOnSubmit } = useLeadFormTracking(listing.id);
  const portalConfigContext = useContext(PortalConfigContext);
  const { leads: portalLeadsConfiguration } = portalConfigContext;

  const setDefaultFormStatus = () => {
    setName('');
    setEmail('');
    setPhone('');
    setMessage(t(messages.contactForm.prePopulatedText));
    setInvalidFields([]);
    setShowError(false);
    setIsDisabled(false);
  };

  const setLeadSuccessState = (leadData) => {
    setDefaultFormStatus();
    setShowSuccess(true);
    onSuccess(leadData);
    setIsDisabled(false);
  };

  const setLeadErrorState = () => {
    setShowError(true);
    setIsDisabled(false);
    onError();
  };

  const handleSubmit = async (shouldConvertCustomLeadHygieneGoal = false) => {
    setIsDisabled(true);
    const checkedInvalidFields = validateAllFields(name, email, phone, message, featureFlagMandatoryPhone);
    const isValidForm = checkedInvalidFields.length === 0;
    if (!isValidForm) {
      setInvalidFields(checkedInvalidFields);
      setIsDisabled(false);
      return;
    }
    const formFields = {
      name,
      email,
      phone: phone ? countryPrefix + ' ' + phone : '',
      comments: message
    };
    const ld = prepareLeadData(
      listing,
      formFields,
      portal,
      portalLeadsConfiguration.types?.boat.type
    );

    const sentResponse = await sendLeadSucceed(ld, portalLeadsConfiguration);
    const isLeadSent = !!sentResponse;
    if (isLeadSent) {
      const { trackType, responseLeadData, leadId } = sentResponse;
      trackOnSubmit(trackType, responseLeadData, leadId, shouldConvertCustomLeadHygieneGoal);
      setLeadSuccessState(responseLeadData);
      const now = new Date();
      localStorage.setItem(`formSubmitted-${listing.id}`, 'true');
      localStorage.setItem(`formSubmitted-${listing.id}-timestamp`, now.getTime());
    } else {
      setLeadErrorState();
    }
  };

  const updateInvalidFields = (fieldType, isValid) => {
    if (isValid) {
      setInvalidFields(invalidFields.filter((field) => field !== fieldType));
    } else {
      if (!invalidFields.includes(fieldType)) {
        const newInvalidFields = [...invalidFields, fieldType];
        setInvalidFields(newInvalidFields);
      }
    }
  };

  const validateAndUpdateField = (fieldType, fieldValue, fieldValidation) => {
    const isValid = validate(fieldValue, fieldValidation || fieldType);
    updateInvalidFields(fieldType, isValid);
  };

  const onChangeName = (e) => setName(e.target.value);
  const onBlurName = (e) => validateAndUpdateField('name', e.target.value, 'fullName');
  const onChangeEmail = (e) => setEmail(e.target.value);
  const onValidateEmail = (e) =>
    validateAndUpdateField('email', e.target.value);
  const onChangeText = (e) => setMessage(e.target.value);
  const onValidateText = (e) => validateAndUpdateField('text', e.target.value);
  const onChangePhone = (e) => setPhone(e.target.value);
  const onBlurPhone = (e) => validateAndUpdateField('phone', e.target.value, 'phoneField');
  const onValidatePhone = featureFlagMandatoryPhone && onBlurPhone;
  const onChangePrefix = (prefix) => setCountryPrefix(prefix);

  const leadFormFields = {
    sellerNameField: prepareLeadSellerName(
      listing,
      handlePhoneCall,
      translator,
      showCloseButton
    ),
    nameField: prepareLeadName(
      idPrefix,
      name,
      translator,
      onChangeName,
      onBlurName
    ),
    emailField: prepareLeadEmail(
      idPrefix,
      email,
      translator,
      onChangeEmail,
      onValidateEmail
    ),
    phoneField: prepareLeadPhone(
      idPrefix,
      phone,
      translator,
      onChangePhone,
      onValidatePhone,
      onChangePrefix,
      featureFlagMandatoryPhone
    ),
    textField: prepareLeadTextarea(
      idPrefix,
      message,
      translator,
      onChangeText,
      onValidateText
    )
  };
  const leadFormHandlers = { showSuccess, showError, handleSubmit };
  return { leadFormFields, leadFormHandlers, invalidFields, isDisabled, selectedCountry, flagsWithPrefix };
};

export { useBdpForm, useLeadFormTracking };
